import { useEffect, useState } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Send, Clock, MapPin, Loader2 } from "lucide-react";

const TZ_PRESETS = [
  { label: "Central (Wisconsin, UTC-6)", value: -360 },
  { label: "Central DST (UTC-5)", value: -300 },
  { label: "Eastern (UTC-5)", value: -300 },
  { label: "Mountain (UTC-7)", value: -420 },
  { label: "Pacific (UTC-8)", value: -480 },
  { label: "UTC", value: 0 },
];

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingBrief, setSendingBrief] = useState(false);
  const [s, setS] = useState({
    morning_briefing_enabled: false,
    morning_briefing_hour: 6,
    morning_briefing_minute: 30,
    timezone_offset_minutes: -360,
    recipient_email: "",
    location: "Madison,WI",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/email/settings`, { withCredentials: true });
        setS(prev => ({ ...prev, ...data, recipient_email: data.recipient_email || "" }));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/email/settings`, s, { withCredentials: true });
      toast.success("Email settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save settings");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await axios.post(`${API}/email/test`, {}, { withCredentials: true });
      toast.success(`Test email sent to ${data.recipient}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test email failed");
    } finally {
      setSendingTest(false);
    }
  };

  const sendBriefNow = async () => {
    setSendingBrief(true);
    try {
      const { data } = await axios.post(
        `${API}/email/morning-briefing/send`, {}, { withCredentials: true }
      );
      toast.success(`Morning brief sent to ${data.recipient}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Brief send failed");
    } finally {
      setSendingBrief(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-3 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading settings…
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 max-w-3xl mx-auto space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Email delivery, notifications, and other preferences.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" /> Morning Briefing Email
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Send briefing email each morning</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Delivered by Resend. Toggle off any time.
              </p>
            </div>
            <Switch
              checked={s.morning_briefing_enabled}
              onCheckedChange={(v) => setS({ ...s, morning_briefing_enabled: v })}
              data-testid="briefing-enable-switch"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> Send time (local)
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={s.morning_briefing_hour}
                  onChange={(e) => setS({ ...s, morning_briefing_hour: parseInt(e.target.value || "0", 10) })}
                  className="w-20"
                  data-testid="briefing-hour-input"
                />
                <span>:</span>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={s.morning_briefing_minute}
                  onChange={(e) => setS({ ...s, morning_briefing_minute: parseInt(e.target.value || "0", 10) })}
                  className="w-20"
                  data-testid="briefing-minute-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Timezone</Label>
              <select
                className="w-full h-9 rounded-md border border-zinc-700 bg-transparent px-2 text-sm"
                value={s.timezone_offset_minutes}
                onChange={(e) => setS({ ...s, timezone_offset_minutes: parseInt(e.target.value, 10) })}
                data-testid="briefing-tz-select"
              >
                {TZ_PRESETS.map((tz) => (
                  <option key={tz.label} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label>Recipient email</Label>
              <Input
                type="email"
                value={s.recipient_email || ""}
                placeholder="you@example.com"
                onChange={(e) => setS({ ...s, recipient_email: e.target.value })}
                data-testid="briefing-recipient-input"
              />
              <p className="text-xs text-muted-foreground">
                While Resend is in test mode, this must match your Resend signup email.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" /> Weather location
              </Label>
              <Input
                value={s.location || ""}
                placeholder="Madison,WI"
                onChange={(e) => setS({ ...s, location: e.target.value })}
                data-testid="briefing-location-input"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={save} disabled={saving} data-testid="save-email-settings-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save settings
            </Button>
            <Button
              variant="outline"
              onClick={sendTest}
              disabled={sendingTest}
              data-testid="send-test-email-btn"
            >
              {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send test email
            </Button>
            <Button
              variant="outline"
              onClick={sendBriefNow}
              disabled={sendingBrief}
              data-testid="send-brief-now-btn"
            >
              {sendingBrief ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Preview today&apos;s brief now
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
